/**
 * Клиент API для SPA: регистрация, подтверждение email, вход и работа с заявкой
 * (загрузка, автосохранение секций, строки спортсменов, «Дуйляня» и представителей).
 * Ошибки сервера приходят в виде {"error": "..."} — их текст показывается пользователю.
 */
import { joinEndpointQuery, type ApiConfig } from '../shared/apiConfig';
import { SESSION_HEADER } from '../shared/sessionHeader';
import type { AuditPeriod, AuditScreen } from '../shared/audit';
import type {
  AdminDocumentFileResponse,
  AdminScreen,
  AdminUser,
  Application,
  Athlete,
  DisciplineId,
  DocumentId,
  DocumentsExportManifest,
  EventInfo,
  OrganizationInfo,
  OrganizerScreen,
  OrganizerTeamView,
  PublicUser,
  Representative,
  Transfer,
  TransferKind,
  UserRole,
} from '../shared/types';
import { getSessionToken } from './session';

/** Карта «путь API → адрес функции»; заполняется из api-config.json (см. shared/apiConfig.ts). */
const apiEndpoints: Record<string, string> = {};

/**
 * Загружает api-config.json (генерируется CI/CD при деплое) и запоминает
 * соответствие путей API URL функций. Если файла нет — остаются относительные пути.
 */
export async function loadApiConfig(): Promise<void> {
  try {
    const response = await fetch('api-config.json', { cache: 'no-store' });
    if (!response.ok) return;
    const config = (await response.json()) as ApiConfig;
    Object.assign(apiEndpoints, config.endpoints);
  } catch {
    // Нет файла — работаем с относительными путями (локальная разработка).
  }
}

/**
 * Подставляет адрес функции по пути API; если соответствия нет — оставляет путь как
 * есть (локальная разработка и контейнерный запуск: API на том же origin). Query
 * запроса уезжает к функции рядом с её `route`-параметром (joinEndpointQuery).
 */
function resolveUrl(path: string): string {
  const queryIndex = path.indexOf('?');
  const pathname = queryIndex === -1 ? path : path.slice(0, queryIndex);
  const endpoint = apiEndpoints[pathname];
  if (!endpoint) return path;
  return joinEndpointQuery(endpoint, queryIndex === -1 ? '' : path.slice(queryIndex));
}

/** Ошибка API: текст для пользователя и HTTP-статус (0 — нет соединения). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Выполняет запрос к API и разбирает JSON-ответ; бросает ApiError при ошибке. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(resolveUrl(path), init);
  } catch {
    throw new ApiError('Нет соединения с сервером. Попробуйте ещё раз.', 0);
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      data && typeof data.error === 'string' ? data.error : `Ошибка сервера (${response.status})`;
    throw new ApiError(message, response.status);
  }
  return data as T;
}

/** POST с JSON-телом для открытых эндпоинтов (регистрация, вход). */
function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Запрос к защищённому API (заявка, документы спортсмена, раздел администратора):
 * токен сессии в собственном заголовке сервиса `X-Eleonora-Session`
 * (src/shared/sessionHeader.ts — почему не `Authorization`: его занимает платформа
 * при прямом вызове функции).
 * Токена нет — сервер ответит 401, экран очистит сессию и вернёт на вход.
 */
function applicationRequest<T>(path: string, method: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers[SESSION_HEADER] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  return request<T>(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/**
 * Поля формы регистрации (совпадают с контрактом POST /api/auth/register).
 * Четыре последних поля — принятие юридических документов (задача 18): два
 * подтверждения и версии принятых редакций. Версии отправляются с запросом, чтобы
 * сервер мог отклонить регистрацию со страницы, открытой до смены редакции.
 */
export interface RegisterPayload {
  firstName: string;
  lastName: string;
  organizationName: string;
  email: string;
  password: string;
  /** Принято ли Пользовательское соглашение (первый чекбокс). */
  termsAccepted: boolean;
  /** Версия редакции Пользовательского соглашения (из src/shared/consents.ts). */
  termsVersion: string;
  /** Принято ли согласие на обработку персональных данных (второй чекбокс). */
  consentAccepted: boolean;
  /** Версия редакции согласия на обработку персональных данных. */
  consentVersion: string;
}

/** Регистрирует пользователя; письмо со ссылкой подтверждения «уходит» заглушкой. */
export function register(payload: RegisterPayload): Promise<{ ok: boolean; message: string }> {
  return postJson('/api/auth/register', payload);
}

/** Подтверждает email по токену из письма (GET /api/auth/confirm?token=...). */
export function confirmEmail(token: string): Promise<{ ok: boolean; message: string }> {
  return request(`/api/auth/confirm?token=${encodeURIComponent(token)}`);
}

/**
 * Запрашивает ссылку восстановления пароля (POST /api/auth/password-reset/request,
 * задача 28). Ответ нейтрален и одинаков для известного и неизвестного адреса —
 * текст с сервера показывается как есть, экран ничего не додумывает.
 */
export function requestPasswordReset(email: string): Promise<{ ok: boolean; message: string }> {
  return postJson('/api/auth/password-reset/request', { email });
}

/**
 * Меняет пароль по токену из письма (POST /api/auth/password-reset/confirm, задача 28).
 * Токен одноразовый: после успеха он больше не сработает, ссылку из адреса страницы
 * убирает экран (`syncAddress`).
 */
export function confirmPasswordReset(
  token: string,
  password: string,
): Promise<{ ok: boolean; message: string }> {
  return postJson('/api/auth/password-reset/confirm', { token, password });
}

/** Входит по email и паролю; возвращает токен сессии и данные пользователя. */
export function login(
  email: string,
  password: string,
): Promise<{ token: string; user: PublicUser }> {
  return postJson('/api/auth/login', { email, password });
}

/**
 * Поля строки спортсмена, которые отправляет таблица спортсменов: столбцы формы и
 * текущий список видов программы. Виды программы строка не редактирует (у них свой
 * диалог), но передаёт вместе с остальными полями: строку собирает `fromValues`
 * секции, поэтому PATCH не может «потерять» уже выбранные виды программы.
 */
export type AthleteFields = Pick<
  Athlete,
  'fullName' | 'birthDate' | 'gender' | 'tshirtSize' | 'disciplines'
>;

/** Поля строки представителя, которые отправляет форма. */
export type RepresentativeFields = Pick<Representative, 'fullName' | 'tshirtSize'>;

/**
 * Загружает текущую заявку пользователя (сервер создаёт её при первом обращении)
 * вместе с описанием мероприятия: из него собирается подпись чекбокса подтверждения
 * участия в конце формы.
 */
export function getApplication(): Promise<{ application: Application; event: EventInfo }> {
  return applicationRequest('/api/application', 'GET');
}

/**
 * Подтверждает участие команды в предстоящем турнире (чекбокс в конце формы):
 * сервер выставляет признак `confirmed` и отправляет письмо организаторам.
 */
export function confirmApplication(): Promise<{ application: Application }> {
  return applicationRequest('/api/application/confirm', 'POST');
}

/** Сохраняет секцию «Организация» (автосохранение). */
export function saveOrganization(
  organization: OrganizationInfo,
): Promise<{ application: Application }> {
  return applicationRequest('/api/application', 'PATCH', { organization });
}

/**
 * Меняет название организации в профиле пользователя
 * (PATCH /api/profile/organization, задача 29).
 *
 * Нужно экрану «название организации занято»: заявку от этой организации уже подал
 * другой тренер, поэтому форма заявки не открывается — сначала сервер проверяет
 * уникальность нового названия (занятое отвечает 409), затем записывает его в профиль
 * и возвращает обновлённый публичный профиль. Экран сохраняет профиль в сессии и
 * открывает форму заявки заново: она создастся уже с новым названием.
 */
export function updateProfileOrganization(
  organizationName: string,
): Promise<{ user: PublicUser }> {
  return applicationRequest('/api/profile/organization', 'PATCH', { organizationName });
}

/** Добавляет спортсмена. */
export function createAthlete(fields: AthleteFields): Promise<{ application: Application }> {
  return applicationRequest('/api/athletes', 'POST', fields);
}

/** Изменяет спортсмена по id. */
export function updateAthlete(
  fields: AthleteFields & { id: string },
): Promise<{ application: Application }> {
  return applicationRequest('/api/athletes', 'PATCH', fields);
}

/** Удаляет спортсмена по id (id передаётся в query — путь плоский, см. apiRoutes.ts). */
export function deleteAthlete(id: string): Promise<{ application: Application }> {
  return applicationRequest(`/api/athletes?id=${encodeURIComponent(id)}`, 'DELETE');
}

/**
 * Сохраняет виды программы спортсмена (диалог «Виды программы»). Список передаётся
 * целиком, поэтому добавление, замена и удаление строки диалога — одна операция:
 * промежуточных состояний на сервере не бывает, а экран берёт ответ как модель.
 */
export function saveAthleteDisciplines(
  athleteId: string,
  disciplines: DisciplineId[],
): Promise<{ application: Application }> {
  return applicationRequest('/api/athletes', 'PATCH', { id: athleteId, disciplines });
}

/** Добавляет представителя. */
export function createRepresentative(
  fields: RepresentativeFields,
): Promise<{ application: Application }> {
  return applicationRequest('/api/representatives', 'POST', fields);
}

/** Изменяет представителя по id. */
export function updateRepresentative(
  fields: RepresentativeFields & { id: string },
): Promise<{ application: Application }> {
  return applicationRequest('/api/representatives', 'PATCH', fields);
}

/** Удаляет представителя по id. */
export function deleteRepresentative(id: string): Promise<{ application: Application }> {
  return applicationRequest(`/api/representatives?id=${encodeURIComponent(id)}`, 'DELETE');
}

/**
 * Сохраняет количество остальных сопровождающих (автосохранение) — поле секции
 * «Представители», а не строка таблицы, поэтому у него отдельный путь: строки
 * сохраняются CRUD `createRepresentative`/`updateRepresentative` выше.
 */
export function saveAccompanyingCount(
  accompanyingCount: number,
): Promise<{ application: Application }> {
  return applicationRequest('/api/representatives/accompanying', 'PATCH', { accompanyingCount });
}

/**
 * Поля строки «Дуйляня», которые отправляет таблица секции: вид программы и
 * идентификаторы выбранных спортсменов. Название вида программы и ФИО в запросе не
 * передаются — их считает сервер (из справочника и из строк заявки), поэтому в
 * выступлении не бывает устаревших имён.
 */
export interface DuilianFields {
  disciplineId: DisciplineId;
  athlete1Id: string;
  athlete2Id: string;
  /** Пустая строка — выступают вдвоём: третий спортсмен не указан. */
  athlete3Id: string;
}

/** Добавляет выступление. */
export function createDuilianEntry(fields: DuilianFields): Promise<{ application: Application }> {
  return applicationRequest('/api/duilian', 'POST', fields);
}

/** Изменяет выступление по id. */
export function updateDuilianEntry(
  fields: DuilianFields & { id: string },
): Promise<{ application: Application }> {
  return applicationRequest('/api/duilian', 'PATCH', fields);
}

/** Удаляет выступление по id (id передаётся в query — путь плоский, см. apiRoutes.ts). */
export function deleteDuilianEntry(id: string): Promise<{ application: Application }> {
  return applicationRequest(`/api/duilian?id=${encodeURIComponent(id)}`, 'DELETE');
}

/**
 * Поля строки приезда/отъезда, которые отправляет таблица подсекции: столбцы формы
 * («Трансфер нужен» — колонка обеих подсекций) плюс «Раннее заселение» (колонка есть
 * только у «Приезда», у «Отъезда» значение всегда false). Название станции не
 * передаётся — оно берётся из справочника по `stationId`.
 */
export type TransferFields = Pick<
  Transfer,
  | 'date'
  | 'time'
  | 'peopleCount'
  | 'stationId'
  | 'transferNeeded'
  | 'trainOrFlightNumber'
  | 'earlyCheckIn'
>;

/** Добавляет строку приезда/отъезда (путь выбирается по подсекции). */
export function createTransfer(
  kind: TransferKind,
  fields: TransferFields,
): Promise<{ application: Application }> {
  return applicationRequest(`/api/transfers/${kind}`, 'POST', fields);
}

/** Изменяет строку приезда/отъезда по id. */
export function updateTransfer(
  kind: TransferKind,
  fields: TransferFields & { id: string },
): Promise<{ application: Application }> {
  return applicationRequest(`/api/transfers/${kind}`, 'PATCH', fields);
}

/** Удаляет строку приезда/отъезда по id (id передаётся в query — путь плоский). */
export function deleteTransfer(
  kind: TransferKind,
  id: string,
): Promise<{ application: Application }> {
  return applicationRequest(`/api/transfers/${kind}?id=${encodeURIComponent(id)}`, 'DELETE');
}

/** Режим передачи файла документа: `proxy` — через бэкенд, `presign` — по подписанной ссылке. */
export type DocumentsMode = 'proxy' | 'presign';

/** Настройки диалога «Документы»: режим передачи файла и лимиты (задача 11). */
export interface DocumentsConfig {
  mode: DocumentsMode;
  /** Предельный размер файла в байтах. */
  maxSizeBytes: number;
  /** Разрешённые MIME-типы (белый список сервера). */
  allowedTypes: string[];
}

/** Настройки загрузки документов: по ним клиент выбирает сценарий и проверяет файл. */
export function getDocumentsConfig(): Promise<DocumentsConfig> {
  return applicationRequest('/api/athletes/documents/config', 'GET');
}

/** Читает файл в base64 (без префикса data:) — так файл уходит на бэкенд в режиме proxy. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new ApiError('Не удалось прочитать файл', 0));
    reader.readAsDataURL(file);
  });
}

/** Прямая загрузка файла по подписанной ссылке — бэкенд в передаче не участвует. */
async function putFileDirectly(url: string, file: File): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
  } catch {
    throw new ApiError('Нет соединения с хранилищем файлов. Попробуйте ещё раз.', 0);
  }
  if (!response.ok) {
    throw new ApiError(`Не удалось загрузить файл (код ${response.status})`, response.status);
  }
}

/**
 * Загружает (или заменяет) файл документа и возвращает заявку целиком.
 *
 * Режим выбирает сервер (его сообщает `getDocumentsConfig`): в `presign` файл идёт
 * прямо в Object Storage по подписанной ссылке, после чего клиент закрепляет
 * метаданные в заявке; в `proxy` файл передаётся бэкенду в base64.
 */
export async function uploadAthleteDocument(
  athleteId: string,
  docId: DocumentId,
  file: File,
  mode: DocumentsMode,
): Promise<{ application: Application }> {
  const metadata = {
    athleteId,
    docId,
    uploadedFileName: file.name,
    contentType: file.type,
    size: file.size,
  };

  if (mode === 'presign') {
    const { uploadUrl } = await applicationRequest<{ uploadUrl: string }>(
      '/api/athletes/documents',
      'POST',
      metadata,
    );
    await putFileDirectly(uploadUrl, file);
    return applicationRequest('/api/athletes/documents/commit', 'POST', metadata);
  }

  return applicationRequest('/api/athletes/documents', 'POST', {
    ...metadata,
    contentBase64: await fileToBase64(file),
  });
}

/**
 * Отдаёт файл браузеру системным скачиванием: Blob → временная ссылка → клик. Общий для
 * документов спортсмена, выгрузки заявок и пониженного режима выгрузки сканов — механика
 * одна, поэтому живёт в одном месте.
 */
export function saveFile(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Ссылку на Blob освобождаем сразу после клика: файл уже отдан браузеру.
  URL.revokeObjectURL(url);
}

/**
 * Байты из base64-строки: так файл приходит в режиме `proxy` (тело ответа обработчика —
 * строка, поэтому бинарные данные едут в JSON). Функция одна и для скачивания документа
 * спортсменом, и для выгрузки сканов администратором.
 */
// Тип возврата указывает буфер явно: `Blob` принимает только вид на `ArrayBuffer`,
// а не на `SharedArrayBuffer` (иначе TS не пропускает `new Blob([bytes])`).
function base64Bytes(contentBase64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** Сохраняет файл, полученный в base64: байты собираются в Blob и скачиваются. */
function saveBase64File(fileName: string, contentType: string, contentBase64: string): void {
  saveFile(fileName || 'документ', new Blob([base64Bytes(contentBase64)], { type: contentType }));
}

/**
 * Скачивает файл документа. В режиме `proxy` сервер отдаёт файл в base64 внутри JSON,
 * в режиме `presign` — подписанную ссылку, по которой файл берётся напрямую.
 */
export async function downloadAthleteDocument(
  athleteId: string,
  docId: DocumentId,
  mode: DocumentsMode,
): Promise<void> {
  const query = `athleteId=${encodeURIComponent(athleteId)}&docId=${encodeURIComponent(docId)}`;

  if (mode === 'presign') {
    const { downloadUrl } = await applicationRequest<{ downloadUrl: string }>(
      `/api/athletes/documents?${query}`,
      'GET',
    );
    window.open(downloadUrl, '_blank', 'noopener');
    return;
  }

  const file = await applicationRequest<{
    fileName: string;
    contentType: string;
    contentBase64: string;
  }>(`/api/athletes/documents?${query}`, 'GET');
  saveBase64File(file.fileName, file.contentType, file.contentBase64);
}

/** Удаляет файл документа (идентификаторы — в теле: путь плоский, см. apiRoutes.ts). */
export function deleteAthleteDocument(
  athleteId: string,
  docId: DocumentId,
): Promise<{ application: Application }> {
  return applicationRequest('/api/athletes/documents', 'DELETE', { athleteId, docId });
}

/**
 * Данные экрана «Администратор» (задача 23): описание турнира и пользователи реестра.
 * Экран открыт только роли administrator — остальным сервер отвечает 403.
 */
export function getAdminScreen(): Promise<AdminScreen> {
  return applicationRequest('/api/admin/screen', 'GET');
}

/**
 * Сохраняет секцию «Турнир»: название, город и сроки. Те же значения показывает
 * чекбокс подтверждения участия в конце формы заявки.
 */
export function saveTournament(event: EventInfo): Promise<{ event: EventInfo }> {
  return applicationRequest('/api/admin/event', 'PATCH', event);
}

/**
 * Сбрасывает признак подтверждения у всех заявок (секция «Действия», сценарий 7).
 * Сервер возвращает число заявок, у которых подтверждение было снято.
 */
export function resetConfirmations(): Promise<{ resetCount: number }> {
  return applicationRequest('/api/admin/confirmations/reset', 'POST');
}

/**
 * Выгрузка всех заявок (секция «Действия» экрана администратора): сервер отдаёт массив
 * заявок, браузер сохраняет его файлом `eleonora-applications-<ГГГГ-ММ-ДД>.json` с
 * отступом в два пробела — файл читают и глазами, и программа интеграции (описание полей
 * — docs/integration/access-export.md). Возвращает число заявок в файле: экран сообщает
 * его пользователю, а при пустой базе это объясняет пустой список в файле.
 */
export async function downloadApplicationsExport(): Promise<number> {
  const applications = await applicationRequest<Application[]>(
    '/api/admin/exports/applications',
    'GET',
  );

  const date = new Date().toISOString().slice(0, 10);
  saveFile(
    `eleonora-applications-${date}.json`,
    new Blob([JSON.stringify(applications, null, 2)], { type: 'application/json' }),
  );
  return applications.length;
}

/** Ссылка на один файл выгрузки сканов: что нужно серверу, чтобы отдать документ. */
export interface DocumentRef {
  /** Владелец заявки — из манифеста выгрузки (ключ объекта строится по нему). */
  ownerUserId: string;
  athleteId: string;
  docId: DocumentId;
}

/**
 * Манифест выгрузки сканов (секция «Действия»): перечень команд, спортсменов и
 * метаданных файлов по подтверждённым заявкам. Архив собирает браузер — файлы
 * забираются по одному (`downloadAdminDocumentFile`), потому что ответ функции
 * ограничен 3,5 МБ, а скан — до 10 МБ.
 */
export function downloadDocumentsManifest(): Promise<DocumentsExportManifest> {
  return applicationRequest('/api/admin/exports/documents', 'GET');
}

/**
 * Забирает один файл выгрузки сканов: в облаке сервер отдаёт подписанную ссылку (файл
 * берётся прямо из Object Storage), в разработке и контейнере — base64 в JSON. Возвращает
 * байты и тип содержимого: по типу выбирается расширение имени файла в архиве.
 */
export async function downloadAdminDocumentFile(
  ref: DocumentRef,
): Promise<{ bytes: Uint8Array<ArrayBuffer>; contentType: string }> {
  const query = new URLSearchParams({
    ownerUserId: ref.ownerUserId,
    athleteId: ref.athleteId,
    docId: ref.docId,
  }).toString();
  const answer = await applicationRequest<AdminDocumentFileResponse>(
    `/api/admin/exports/documents/file?${query}`,
    'GET',
  );

  if (answer.mode === 'presign') {
    let response: Response;
    try {
      response = await fetch(answer.downloadUrl);
    } catch {
      throw new ApiError('Нет соединения с хранилищем файлов. Попробуйте ещё раз.', 0);
    }
    if (!response.ok) {
      throw new ApiError(`Не удалось скачать файл (код ${response.status})`, response.status);
    }
    return {
      bytes: new Uint8Array(await response.arrayBuffer()),
      contentType: response.headers.get('Content-Type') ?? '',
    };
  }

  return { bytes: base64Bytes(answer.contentBase64), contentType: answer.contentType };
}

/** Назначает роли пользователю (секция «Пользователи»): передаются только назначаемые роли
 * (organizer, administrator) — пустой список снимает обе, базовая «Пользователь» же
 * есть у любого аккаунта и не передаётся.
 */
export function setUserRoles(userId: string, roles: UserRole[]): Promise<{ user: AdminUser }> {
  return applicationRequest('/api/admin/users/roles', 'PATCH', { userId, roles });
}

/**
 * Меняет признаки пользователя (секция «Пользователи»): «Подтверждённый» — подтверждение
 * администратором (заявка неподтверждённого пользователя не учитывается в таблицах
 * раздела организатора), «Активный» — право входа в сервис. Экран отправляет оба признака
 * сразу: состояние строки на его стороне одно, поэтому сервер не гадает, что осталось прежним.
 */
export function setUserFlags(
  userId: string,
  flags: { approved: boolean; active: boolean },
): Promise<{ user: AdminUser }> {
  return applicationRequest('/api/admin/users/flags', 'PATCH', { userId, ...flags });
}

/**
 * Удаляет пользователя и все его данные (секция «Пользователи»): заявку, документы,
 * назначенные роли и запись реестра. Сервер возвращает оставшихся пользователей — по
 * ним таблица перерисовывается, как и после смены ролей. Идентификатор передаётся в
 * адресе: так же его передают остальные `DELETE` сервиса.
 */
export function deleteUser(userId: string): Promise<{ users: AdminUser[] }> {
  return applicationRequest(`/api/admin/users?userId=${encodeURIComponent(userId)}`, 'DELETE');
}

/**
 * Журнал аудита (секция «Аудит»): записи за скользящее окно периода — сутки или
 * неделя. Период передаётся query-параметром: так экран переключает окно без
 * перезагрузки и без второго эндпоинта.
 *
 * `etag` — версия последней показанной таблицы: с ней запрос становится условным, и
 * неизменившийся журнал приходит ответом `304` без тела (задача 38, этап B).
 */
export function fetchAdminAudit(
  period: AuditPeriod,
  etag?: string,
): Promise<ConditionalResponse<AuditScreen>> {
  return conditionalGet<AuditScreen>(`/api/admin/audit?period=${period}`, etag);
}

/**
 * Ответ раздела «Организатор» (задача 35, этап D). Пустой `screen` означает ответ `304`:
 * версия на сервере не изменилась, браузер переиспользует сохранённый экран.
 */
export interface OrganizerScreenResponse {
  /** Свежий экран или undefined при `304` (`notModified`). */
  screen?: OrganizerScreen;
  /** Версия тела ответа (`ETag`) — её отправляет следующий запрос в `If-None-Match`. */
  etag?: string;
  /** Сервер подтвердил, что сохранённая версия актуальна: тела в ответе нет. */
  notModified: boolean;
}

/**
 * Ответ условного GET: тело (если оно есть), версия ответа (`ETag`) и признак «не
 * изменилось». Пустое `data` при `notModified` означает: сервер подтвердил версию, и
 * SPA показывает то, что уже сохранено в памяти (`organizerCache`, `adminAuditCache`).
 */
export interface ConditionalResponse<T> {
  /** Свежие данные или undefined при `304` (`notModified`). */
  data?: T;
  /** Версия тела ответа (`ETag`) — её отправляет следующий запрос в `If-None-Match`. */
  etag?: string;
  /** Сервер подтвердил, что сохранённая версия актуальна: тела в ответе нет. */
  notModified: boolean;
}

/**
 * GET защищённого API с необязательным `If-None-Match`: `304` — не ошибка, а «версия та
 * же». Токен сессии идёт своим заголовком сервиса (см. `applicationRequest`), версия —
 * заголовком `If-None-Match`; иначе браузер не получит `304` (задача 35, этап D).
 */
async function conditionalGet<T>(path: string, etag?: string): Promise<ConditionalResponse<T>> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers[SESSION_HEADER] = `Bearer ${token}`;
  if (etag) headers['If-None-Match'] = etag;

  let response: Response;
  try {
    response = await fetch(resolveUrl(path), { method: 'GET', headers });
  } catch {
    throw new ApiError('Нет соединения с сервером. Попробуйте ещё раз.', 0);
  }

  if (response.status === 304) return { notModified: true };

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      data && typeof data.error === 'string' ? data.error : `Ошибка сервера (${response.status})`;
    throw new ApiError(message, response.status);
  }

  return {
    data: data as T,
    etag: response.headers.get('ETag') ?? undefined,
    notModified: false,
  };
}

/**
 * Данные экрана «Организатор» (задача 24): четыре таблицы по подтверждённым заявкам —
 * «Подтверждённые заявки», «Приезды», «Отъезды» и «Футболки». Экран открыт только роли
 * организатора — остальным сервер отвечает 403.
 *
 * `etag` — версия последнего показанного экрана: если она передана, запрос становится
 * условным, и сервер при неизменных данных отвечает `304` без тела (задача 35, этап D).
 */
export async function fetchOrganizerScreen(etag?: string): Promise<OrganizerScreenResponse> {
  const response = await conditionalGet<OrganizerScreen>('/api/organizer/screen', etag);
  return { screen: response.data, etag: response.etag, notModified: response.notModified };
}

/**
 * Заявка одной команды для экрана «Заявка команды» (задача 43): состав, выступления,
 * представители и трансферы. Экран открывается из раздела «Организатор» по ссылке на
 * названии команды, адрес команды (`ownerUserId`) приходит в адресе страницы.
 *
 * Ответ не кешируется (сервер отвечает `Cache-Control: private, no-store`), и условного
 * запроса у экрана нет: состав команды читается при каждом открытии — тренер правит
 * заявку вплоть до регистрационного собрания. `404` здесь — обычный исход («заявка
 * команды не найдена»): команду могли снять с подтверждения, пока раздел был открыт.
 */
export function fetchOrganizerTeam(ownerUserId: string): Promise<OrganizerTeamView> {
  const query = `ownerUserId=${encodeURIComponent(ownerUserId)}`;
  return applicationRequest<OrganizerTeamView>(`/api/organizer/team?${query}`, 'GET');
}